import { formattedDate, toDate } from 'shared/utils/date';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { ReportAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { ReportsTable } from '../ReportGroup';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  INDICATOR_STAGES_KEY,
  IndicatorByService,
} from '../../ReportBuilder/Components/ImpactIndicator/utils';
import { SectionIndicators } from '../../ReportBuilder/Components/IndicatorDetails/utils';
import {
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
} from '../../NPOFeedback/utils';
import {
  DIRECT_COST_QUESTION_ID,
  FINAL_BUDGET_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
  saveFinalBudget,
} from '../../ReportBuilder/Components/Budget/utils';
import {
  NARRATIVE_LESSON_QUESTION_ID,
  NARRATIVE_STORY_QUESTION_ID,
} from '../../ReportBuilder/Components/Narrative/utils';
import {
  DESCRIPTION_QUESTION_ID,
  LOCATIONS_QUESTION_ID,
} from '../../ReportBuilder/Components/ReportFinalize/answersUtil';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';

interface Taxonomy {
  Definition: string;
  Icon: string;
  Level_1: string;
  Level_2: string;
  Level_3: string;
  Level_4: string;
  PCS_code: string;
  Subject: string;
  Subject_level: number;
}

const parseTaxonomy = (
  t: string | null,
): { values: Taxonomy[] } | undefined => {
  if (!t) return undefined;
  try {
    return JSON.parse(t);
  } catch (e) {
    return undefined;
  }
};

const mapImpactSummary = (
  r: ReportsTable,
): {
  impactSummary: IndicatorByService;
  indicatorDetails: SectionIndicators;
} => {
  const impactSummary: IndicatorByService = {
    act: [],
    learn: [],
    reach: [],
    succeed: [],
    catalyzed: [],
    capacityDevelopment: [],
  };
  const indicatorDetails: SectionIndicators = {
    act: [],
    learn: [],
    reach: [],
    succeed: [],
    catalyzed: [],
    capacityDevelopment: [],
  };

  for (const i of r.results) {
    if (!i.relevant || !i.enabled) continue;
    const stageId = INDICATOR_STAGES_KEY[i.indicator.stageId];
    impactSummary[stageId].push({
      id: i.indicatorId,
      definition: i.indicator.definition,
      name: i.indicator.name,
      seen: true,
      stageId: i.indicator.stageId,
      stgName: i.indicator.stgName,
      version: 'v3',
      customizable: true,
      lockDelete: false,
      updatedText: i.successCriteria,
      unit: i.indicator.unit,
      definitionStart: i.indicator.definitionStart,
      definitionEnd: i.indicator.definitionEnd,
      initialForecastStart: i.indicator.initialForecastStart,
      initialForecastEnd: i.indicator.initialForecastEnd,
      initialMeasurementStart: i.indicator.initialMeasurementStart,
      initialMeasurementEnd: i.indicator.initialMeasurementEnd,
      impactReceiptText: i.indicator.impactReceiptText,
      outcome: i.indicator.outcome,
      forecastOutcome: i.indicator.forecastOutcome,
      finalOutcome: i.indicator.finalOutcome,
      finalForecastStart: i.indicator.finalForecastStart,
      finalForecastEnd: i.indicator.finalForecastEnd,
      finalMeasurementStart: i.indicator.finalMeasurementStart,
      finalMeasurementEnd: i.indicator.finalMeasurementEnd,
      isHuman: i.indicator.isHuman,
    });
    const file: TrueImpactFile | undefined = i.description
      ? {
          description: i.description,
        }
      : undefined;
    indicatorDetails[stageId].push({
      indicatorDefinition: i.successCriteria,
      indicatorId: i.indicatorId,
      indicatorName: i.indicator.name,
      stageId: i.indicator.stageId,
      file: file ? JSON.stringify(file) : undefined,
      forecast: i.forecastInitial || 0,
      finalForecast: i.forecastCurrent || 0,
      measurement: i.classification || undefined,
    });
  }
  return { impactSummary, indicatorDetails };
};

const setAnswer = (
  questionId: number,
  answer: string,
  sectionId: FeedbackSectionId,
  date1?: string,
  date2?: string,
): Answer => {
  return {
    date1: date1 || null,
    date2: date2 || null,
    dbId: questionId,
    question: {
      dbId: questionId,
      section: sectionId,
      text: answer,
      questionType: 'RADIO',
    },
    questionId,
    questionText: '',
    questionType: 'RADIO',
    text: answer,
  };
};

export const mapReportToAnswers = (r: ReportsTable): ReportAnswers => {
  const isFinalReport = r.stage?.toUpperCase() === 'FINAL';

  const result: ReportAnswers = {
    activities: [],
    dates: [],
    budget: [],
    narrative: [],
    beneficiaries: [],
    description: [],
    location: [],
    impact_summary: [],
    indicator_details: [],
    finalize_report: [],
  };
  result.description?.push(
    setAnswer(
      DESCRIPTION_QUESTION_ID,
      r.reportDescription || r.beneficiaryDescription,
      'description',
    ),
  );
  const startDate = toDate(r.startDate);
  const endDate = toDate(r.endDate);
  if (startDate && endDate) {
    result.dates?.push(
      setAnswer(
        10003,
        `CustomYear: ${formattedDate(startDate)} - ${formattedDate(endDate)}`,
        'dates',
      ),
    );
  }

  if (r.results.length) {
    const { impactSummary, indicatorDetails } = mapImpactSummary(r);
    result.impact_summary?.push(
      setAnswer(
        IMPACT_SUMMARY_QUESTION_ID,
        JSON.stringify(impactSummary),
        'impact_summary',
      ),
    );
    result.indicator_details?.push(
      setAnswer(
        INDICATOR_DETAILS_QUESTION_ID,
        JSON.stringify(indicatorDetails),
        'indicator_details',
      ),
    );
  }

  const taxonomy = parseTaxonomy(r.taxonomySubjectLevel)?.values;
  if (isFinalReport && taxonomy && taxonomy.length > 0) {
    result.narrative?.push(
      setAnswer(
        NARRATIVE_STORY_QUESTION_ID,
        JSON.stringify({
          text: {
            fileId: undefined,
            fileName: undefined,
            title: taxonomy[0].Subject,
            text: r.successStory || taxonomy[0].Definition,
            url: undefined,
          },
          answerId: 0,
          type: 'upload_photo',
        }),
        'narrative',
      ),
    );
    result.narrative?.push(
      setAnswer(
        NARRATIVE_LESSON_QUESTION_ID,
        JSON.stringify({
          text: {
            text:
              r.lessonLearned ||
              taxonomy.map(x => x.Definition).join('\n') ||
              null,
            lesson: taxonomy.map(x => x.Subject).join(', '),
          },
          answerId: 0,
          type: 'categories_lesson',
        }),
        'narrative',
      ),
    );
  }

  result.location?.push(
    setAnswer(
      LOCATIONS_QUESTION_ID,
      JSON.stringify(
        (r.beneficiaryLocations || []).map(x => ({
          address: x.location,
          percentage: 100,
        })),
      ),
      'location',
    ),
  );

  result.budget?.push(
    setAnswer(
      DIRECT_COST_QUESTION_ID,
      JSON.stringify({
        description: r.costCommentDirectCash + ' ' + r.costCommentDirectInKind,
        currency: (r.costInitialDirectCash || 0).toString(),
      }),
      'budget',
    ),
  );
  result.budget?.push(
    setAnswer(
      OVERHEAD_COST_QUESTION_ID,
      JSON.stringify({
        currency: (r.costInitialIndirectCash || 0).toString(),
      }),
      'budget',
    ),
  );
  result.budget?.push(
    setAnswer(
      IN_KIND_DONATIONS_QUESTION_ID,
      JSON.stringify({
        currency: (r.costInitialDirectInKind || 0).toString(),
      }),
      'budget',
    ),
  );
  // result.budget?.push(
  //   setAnswer(
  //     BUDGET_FILE_QUESTION_ID,
  //     JSON.stringify({
  //       fileName: [],
  //       description: 'Budget File',
  //     }),
  //     'budget',
  //   ),
  // );
  if (isFinalReport) {
    result.finalize_report?.push(
      setAnswer(
        FINAL_BUDGET_QUESTION_ID,
        JSON.stringify(
          saveFinalBudget(
            (r.costCurrentDirectCash || 0).toString(),
            (r.costCurrentIndirectCash || 0).toString(),
            (r.costCurrentDirectInKind || 0).toString(),
          ),
        ),
        'finalize_report',
      ),
    );
  }

  return result;
};
